"use client";

import { GA4DataLayer } from "@packages/lib/utlils/helper-function";
import { GA4EventAttrType } from "@packages/lib/types/datalayerGA";
import { GAConstants } from "@packages/shared-components/services/constants";

/**
 * GA Tracking utility for the Exit Intent Form popup.
 *
 * Implements the Whatuni site tracking requirements from Confluence:
 * "Whatuni Revamp - Overall Technical Requirement 6" (Exit Intent Page table)
 * Jira: P4DVS-88
 *
 * Events:
 * - exit_intent_pop_up_viewed    (track_event) - popup opens
 * - exit_intent_email_started    (track_event) - user starts typing email
 * - exit_intent_pop_up_closed    (track_event) - popup closed
 * - registration                 (track_kpi)   - form submitted successfully
 *
 * Key Parameters:
 * - click_text: source CTA that triggered the popup (e.g. "visit_website",
 *   "book_open_day", "get_prospectus", "request_info")
 * - year_of_entry: the year selected in the popup
 * - field: "email_started" when the user has started filling the email on close,
 *   otherwise "na"
 * - page_name: read from localStorage "gaPageName" (same logic as rest of site)
 */

const getPageName = (): string => {
  if (typeof localStorage === "undefined") return GAConstants.notApplicable;
  return localStorage.getItem("gaPageName") || GAConstants.notApplicable;
};

/** Fires when the exit intent popup is opened/viewed */
export const trackExitIntentViewed = (clickText: string) => {
  GA4DataLayer({
    event: GAConstants.track_event,
    event_name: "exit_intent_pop_up_viewed",
    page_name: getPageName(),
    click_text: clickText || GAConstants.notApplicable,
  } as GA4EventAttrType);
};

/** Fires when the user starts filling in the email address */
export const trackExitIntentEmailStarted = (
  clickText: string,
  yearOfEntry: string,
) => {
  GA4DataLayer({
    event: GAConstants.track_event,
    event_name: "exit_intent_email_started",
    page_name: getPageName(),
    click_text: clickText || GAConstants.notApplicable,
    year_of_entry: yearOfEntry || GAConstants.notApplicable,
  } as GA4EventAttrType);
};

/** Fires when the exit intent popup is closed */
export const trackExitIntentClosed = (
  clickText: string,
  yearOfEntry: string,
  emailStarted: boolean,
) => {
  GA4DataLayer({
    event: GAConstants.track_event,
    event_name: "exit_intent_pop_up_closed",
    page_name: getPageName(),
    click_text: clickText || GAConstants.notApplicable,
    year_of_entry: yearOfEntry || GAConstants.notApplicable,
    field: emailStarted ? "email_started" : GAConstants.notApplicable,
  } as GA4EventAttrType);
};

/** Fires when the exit intent form is submitted successfully */
export const trackExitIntentRegistration = (
  clickText: string,
  yearOfEntry: string,
) => {
  GA4DataLayer({
    event: GAConstants.track_kpi,
    event_name: "registration",
    page_name: getPageName(),
    click_text: clickText || GAConstants.notApplicable,
    year_of_entry: yearOfEntry || GAConstants.notApplicable,
  } as GA4EventAttrType);
};
